import { supabase } from '../lib/supabase';
import { DEMO_ERROR_MESSAGE, isDemoCurrentUser } from '../utils/demoMode';

export const nearMissService = {
  /**
   * Create a new near miss record.
   */
  async createNearMiss(data) {
    try {
      if (await isDemoCurrentUser()) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', user?.id)
        ?.single();

      const insertData = {
        company_id: profile?.company_id,
        site_id: data?.site_id,
        reported_by_user_id: user?.id,
        date: data?.date,
        location: data?.location || null,
        description: data?.description,
        risk_level: data?.risk_level,
        status: 'Open',
      };

      const { data: result, error } = await supabase
        ?.from('near_misses')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to create near miss' };
    }
  },

  /**
   * Get all near misses for a company with optional filters.
   *
   * NSherjFh PR 5: default-filters soft-deleted near-misses. Pass
   * `{ includeDeleted: true }` (third arg) from the near-misses
   * 'View deleted' tab. The risk_level / status filters are separate
   * axes from deleted_at and continue to operate within the already-
   * filtered result set.
   *
   * The `photos:near_miss_photos(id)` embed is a count-only embed (no
   * per-photo soft-delete UI exists yet), so the embed is intentionally
   * NOT filtered on near_miss_photos.deleted_at — same result today.
   * When a per-photo Delete action lands, the embed should grow a
   * deleted_at filter via the PostgREST referencedTable option.
   */
  async getNearMissesForCompany(companyId, filters = {}, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('near_misses')
        ?.select(`
          *,
          site:sites!near_misses_site_id_fkey(id, site_name),
          reporter:user_profiles!near_misses_reported_by_user_id_fkey(id, full_name),
          photos:near_miss_photos(id)
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (!includeDeleted) query = query?.is('deleted_at', null);

      if (filters?.site_id) {
        query = query?.eq('site_id', filters?.site_id);
      }
      if (filters?.risk_level && filters?.risk_level !== 'All') {
        query = query?.eq('risk_level', filters?.risk_level);
      }
      if (filters?.status && filters?.status !== 'All') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.date_from) {
        query = query?.gte('date', filters?.date_from);
      }
      if (filters?.date_to) {
        query = query?.lte('date', filters?.date_to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load near misses' };
    }
  },

  /**
   * Get near misses for a specific site.
   */
  async getNearMissesForSite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase
        ?.from('near_misses')
        ?.select(`
          *,
          site:sites!near_misses_site_id_fkey(id, site_name),
          reporter:user_profiles!near_misses_reported_by_user_id_fkey(id, full_name),
          photos:near_miss_photos(id)
        `)
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load near misses for site' };
    }
  },

  /**
   * Update the status of a near miss.
   */
  async updateNearMissStatus(id, status) {
    try {
      if (await isDemoCurrentUser()) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }

      const { data, error } = await supabase
        ?.from('near_misses')
        ?.update({ status })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to update status' };
    }
  },

  /**
   * NSherjFh PR 5 — recoverable soft-delete.
   *
   * Previously a hard DELETE called by near-misses/index.jsx's
   * window.confirm flow. Now an idempotent UPDATE stamping deleted_at
   * + deleted_by. The near_miss_photos rows + photo files in the
   * near-miss-photos bucket stay intact so restore brings everything
   * back. Mirrors PR 2-4 exactly. Returns null data on a re-click of
   * an already-deleted row (.is('deleted_at', null) gate + maybeSingle).
   */
  async deleteNearMiss(id) {
    try {
      if (await isDemoCurrentUser()) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('near_misses')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', id)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to delete near miss' };
    }
  },

  /**
   * Inverse of deleteNearMiss. Clears deleted_at + deleted_by so the
   * report reappears across every active surface. Existing
   * near_miss_photos remain in the DB (preemptive deleted_at column
   * on that table is unrelated to parent restore), so they surface
   * automatically when the parent unblocks.
   */
  async restoreNearMiss(id) {
    try {
      if (await isDemoCurrentUser()) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('near_misses')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', id)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to restore near miss' };
    }
  },

  /**
   * Upload a photo linked to a near miss.
   */
  async uploadNearMissPhoto(file, nearMissId) {
    try {
      if (await isDemoCurrentUser()) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      const ext = file?.name?.split('.')?.pop();
      const filePath = `${nearMissId}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('near-miss-photos')
        ?.upload(filePath, file, { upsert: false });

      if (uploadError) throw uploadError;

      const { data: photoRecord, error: dbError } = await supabase
        ?.from('near_miss_photos')
        ?.insert({
          near_miss_id: nearMissId,
          photo_path: filePath,
          uploaded_by: user?.id,
        })
        ?.select()
        ?.single();

      if (dbError) throw dbError;
      return { data: photoRecord, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to upload photo' };
    }
  },

  /**
   * Get all photos for a near miss with signed URLs.
   */
  async getNearMissPhotos(nearMissId) {
    try {
      const { data: photos, error } = await supabase
        ?.from('near_miss_photos')
        ?.select('*')
        ?.eq('near_miss_id', nearMissId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      // Generate signed URLs for each photo
      const photosWithUrls = await Promise.all(
        (photos || [])?.map(async (photo) => {
          const { data: urlData } = await supabase?.storage
            ?.from('near-miss-photos')
            ?.createSignedUrl(photo?.photo_path, 3600);
          return { ...photo, url: urlData?.signedUrl || null };
        })
      );

      return { data: photosWithUrls, error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load photos' };
    }
  },

  /**
   * Count open near misses for a company (used by compliance dashboard).
   */
  async getOpenNearMissCount(companyId) {
    try {
      const { count, error } = await supabase
        ?.from('near_misses')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.eq('status', 'Open')
        // NSherjFh PR 5: compliance Open-count excludes soft-deleted
        // near-misses (they're 'gone' from active view regardless of
        // their pre-delete status).
        ?.is('deleted_at', null);

      if (error) throw error;
      return { count: count || 0, error: null };
    } catch (err) {
      return { count: 0, error: err?.message };
    }
  },
};
